(() => {
  if (window.UniversalPdp?.initialize) {
    window.UniversalPdp.initialize();
    return;
  }

  const rootSelector = "[data-universal-pdp]";
  const accordionSelector = "[data-universal-accordion]";
  const initializedAttribute = "data-universal-pdp-initialized";
  const controllers = new WeakMap();

  function isVisiblyActionable(control, boundary) {
    if (!control || control.disabled || control.hidden || control.inert) return false;
    if (control.getAttribute?.("aria-hidden") === "true" || control.getAttribute?.("aria-disabled") === "true") return false;
    if (control.closest?.("fieldset[disabled]")) return false;

    let element = control;
    while (element) {
      if (element.hidden || element.inert || element.hasAttribute?.("inert")) return false;
      if (element.getAttribute?.("aria-hidden") === "true" || element.getAttribute?.("aria-disabled") === "true") return false;
      if (typeof window.getComputedStyle === "function") {
        const style = window.getComputedStyle(element);
        if (style?.display === "none" || style?.visibility === "hidden" || style?.pointerEvents === "none") return false;
      }
      const parent = element.parentElement;
      const host = !parent ? element.getRootNode?.()?.host : null;
      element = parent || (host && host !== element ? host : null);
    }
    return true;
  }

  function bundleIsReady(bundle) {
    const surface = bundle?.shadowRoot || bundle;
    if (!surface?.querySelectorAll) return false;
    const controls = [...surface.querySelectorAll('button, input[type="submit"], input[type="button"], [role="button"]')];
    return controls.some((control) => {
      if (!isVisiblyActionable(control, surface)) return false;
      const type = String(control.type || control.getAttribute?.("type") || "").toLowerCase();
      if (type === "select") return false;
      const label = [control.textContent, control.value, control.getAttribute?.("aria-label")]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      return /\b(?:add|select)\b[\s\S]*\b(?:cart|item|bundle)\b|\bbuy\b/i.test(label);
    });
  }

  function syncBundleState(root) {
    const bundle = root.querySelector("cartmax-product-recommend");
    const ready = bundleIsReady(bundle);
    const readyValue = String(ready);
    if (root.getAttribute?.("data-easy-bundle-ready") !== readyValue) {
      root.setAttribute("data-easy-bundle-ready", readyValue);
    }

    const stickyButton = root.querySelector("[data-universal-sticky-atc] .m-add-to-cart");
    const stickyText = stickyButton?.querySelector(".m-add-to-cart--text");
    if (!stickyText) return ready;

    const overrideText = "Choose bundle options";
    const currentText = stickyText.textContent.trim();
    if (ready) {
      if (currentText !== overrideText) stickyText.dataset.easyBundleNativeText = currentText;
      stickyText.dataset.easyBundleOverride = "true";
      if (currentText !== overrideText) stickyText.textContent = overrideText;
      return true;
    }

    if (stickyText.dataset.easyBundleOverride === "true") {
      const nativeText = stickyText.dataset.easyBundleNativeText;
      if (currentText === overrideText && nativeText) stickyText.textContent = nativeText;
      delete stickyText.dataset.easyBundleOverride;
      delete stickyText.dataset.easyBundleNativeText;
    }
    return false;
  }

  class UniversalPdp {
    constructor(root) {
      this.root = root;
      this.root.setAttribute(initializedAttribute, "true");
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.root.addEventListener("click", this.onClick);
      this.root.addEventListener("change", this.onChange);
      if (typeof MutationObserver === "function") {
        this.bundleMountObserver = new MutationObserver(() => {
          const surface = this.bundle?.shadowRoot || this.bundle;
          if (surface !== this.bundleSurface) this.syncBundleState();
        });
        this.bundleMountObserver.observe(this.root, { childList: true, subtree: true });
      }
      if (typeof window.setTimeout === "function") {
        [0, 250, 1000, 3000, 8000].forEach((delay) => window.setTimeout(() => this.syncBundleState(), delay));
      }
      this.reconcile();
    }

    get quantityInput() {
      return this.root.querySelector('.m-product-form--main input[name="quantity"]');
    }

    get shortcutButtons() {
      return [...this.root.querySelectorAll("[data-universal-quantity]")];
    }

    get bundle() {
      return this.root.querySelector("cartmax-product-recommend");
    }

    observeBundleSurface(bundle) {
      const surface = bundle?.shadowRoot || bundle || null;
      if (surface === this.bundleSurface) return;
      this.bundleSurfaceObserver?.disconnect();
      this.bundleSurface = surface;
      if (!surface || typeof MutationObserver !== "function") return;
      this.bundleSurfaceObserver = new MutationObserver(() => this.syncBundleState());
      const attributeFilter = ["aria-disabled", "aria-hidden", "class", "disabled", "hidden", "inert", "style"];
      this.bundleSurfaceObserver.observe(surface, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter,
      });
      if (bundle !== surface) this.bundleSurfaceObserver.observe(bundle, { attributes: true, attributeFilter });
    }

    syncBundleState() {
      const bundle = this.bundle;
      this.observeBundleSurface(bundle);
      return syncBundleState(this.root);
    }

    onClick(event) {
      const stickyButton = event.target.closest("[data-universal-sticky-atc] .m-add-to-cart");
      const bundle = this.bundle;
      if (stickyButton && bundle && this.syncBundleState() && this.root.contains(stickyButton)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!bundle.hasAttribute("tabindex")) bundle.setAttribute("tabindex", "-1");
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        bundle.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
        bundle.focus({ preventScroll: true });
        return;
      }

      const shortcut = event.target.closest("[data-universal-quantity]");
      if (!shortcut || shortcut.disabled || !this.root.contains(shortcut)) return;

      const input = this.quantityInput;
      if (!input) return;
      input.value = shortcut.dataset.universalQuantity;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }

    onChange(event) {
      if (event.target === this.quantityInput) this.reconcile();
    }

    onVariantChanged(event) {
      if (event.detail?.sectionId && event.detail.sectionId !== this.root.dataset.sectionId) return;
      const variant = event.detail?.variant;
      if (!variant) return;
      this.reconcile(variant.price);
    }

    reconcile(variantPrice) {
      this.syncBundleState();
      const input = this.quantityInput;
      if (!input) return;

      const value = Number(input.value);
      const minimum = Number(input.min || input.dataset.min || 1);
      const maximum = input.max ? Number(input.max) : null;
      const increment = Number(input.step || 1);

      this.shortcutButtons.forEach((button) => {
        const quantity = Number(button.dataset.universalQuantity);
        const legal = quantity >= minimum
          && (maximum === null || quantity <= maximum)
          && (quantity - minimum) % increment === 0;
        const selected = legal && quantity === value;

        button.disabled = !legal;
        button.setAttribute("aria-disabled", String(!legal));
        button.setAttribute("aria-checked", String(selected));
        button.classList.toggle("is-selected", selected);

        const subtotal = button.querySelector("[data-universal-subtotal]");
        if (subtotal && Number.isFinite(variantPrice)) {
          const cents = variantPrice * quantity;
          if (typeof formatMoney === "function") {
            subtotal.innerHTML = formatMoney(cents, window.MinimogSettings.money_format);
          } else {
            const currency = window.Shopify?.currency?.active || "USD";
            const locale = document.documentElement?.lang || undefined;
            try {
              subtotal.textContent = new Intl.NumberFormat(locale, { style: "currency", currency }).format(cents / 100);
            } catch (_error) {
              subtotal.textContent = (cents / 100).toFixed(2);
            }
          }
        }
      });
    }
  }

  class UniversalAccordion {
    constructor(root) {
      this.root = root;
      this.root.setAttribute(initializedAttribute, "true");
      this.root.addEventListener("click", (event) => {
        const button = event.target.closest("[data-universal-accordion-button]");
        if (!button || !this.root.contains(button)) return;
        const panel = document.getElementById(button.getAttribute("aria-controls"));
        if (!panel) return;
        const expanded = button.getAttribute("aria-expanded") === "true";
        button.setAttribute("aria-expanded", String(!expanded));
        panel.hidden = expanded;
      });
    }
  }

  function guardNativeCollapsibleFocus(root) {
    root.querySelectorAll("collapsible-tab").forEach((tab) => {
      if (tab.hasAttribute("data-universal-focus-guard")) return;
      const trigger = tab.querySelector("[data-trigger]");
      const content = tab.querySelector("[data-content]");
      if (!trigger || !content) return;

      const sync = () => {
        content.toggleAttribute("inert", trigger.getAttribute("aria-expanded") !== "true");
      };

      tab.setAttribute("data-universal-focus-guard", "true");
      sync();
      new MutationObserver(sync).observe(trigger, { attributes: true, attributeFilter: ["aria-expanded"] });
    });
  }

  function initializeReviews(scope = document) {
    const sections = [];
    if (scope.matches?.("[data-universal-review-section]")) sections.push(scope);
    sections.push(...scope.querySelectorAll?.("[data-universal-review-section]") || []);
    sections.forEach((section) => {
      if (section.hasAttribute("data-review-initialized")) return;
      section.setAttribute("data-review-initialized", "true");
      const limit = Number(section.dataset.reviewLimit || 4);
      const cards = [...section.querySelectorAll(".universal-pdp-review")];
      const more = section.querySelector("[data-review-show-more]");
      const setExpanded = (expanded) => {
        cards.forEach((card, index) => {
          card.hidden = !expanded && index >= limit;
          if (card.hidden) card.querySelectorAll("video").forEach((video) => video.pause());
        });
        if (!more) return;
        more.setAttribute("aria-expanded", String(expanded));
        more.textContent = expanded ? section.dataset.labelLess : section.dataset.labelMore;
      };
      if (more) {
        more.hidden = cards.length <= limit;
        more.addEventListener("click", () => setExpanded(more.getAttribute("aria-expanded") !== "true"));
      }
      setExpanded(false);

      section.querySelectorAll(".universal-pdp-review__details").forEach((details) => {
        const excerpt = details.previousElementSibling;
        const summary = details.querySelector("[data-review-read-more]");
        details.addEventListener("toggle", () => {
          if (excerpt) excerpt.hidden = details.open;
          if (summary) summary.textContent = details.open ? section.dataset.labelLess : summary.dataset.labelMore || summary.textContent;
        });
        if (summary) summary.dataset.labelMore = summary.textContent;
      });

      const lightbox = section.nextElementSibling?.matches?.("[data-universal-review-lightbox]")
        ? section.nextElementSibling
        : null;
      const image = lightbox?.querySelector("[data-universal-review-lightbox-image]");
      section.addEventListener("click", (event) => {
        const button = event.target.closest("[data-review-image]");
        if (!button || !section.contains(button) || !lightbox || !image) return;
        image.src = button.dataset.reviewImage;
        lightbox.showModal();
      });
      lightbox?.addEventListener("close", () => { if (image) image.removeAttribute("src"); });
    });
  }

  function initialize(scope = document) {
    const roots = [];
    if (scope.matches?.(rootSelector)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(rootSelector) || []);

    roots.forEach((root) => {
      if (root.hasAttribute(initializedAttribute)) return;
      const controller = new UniversalPdp(root);
      controllers.set(root, controller);
      guardNativeCollapsibleFocus(root);
    });

    const accordions = [];
    if (scope.matches?.(accordionSelector)) accordions.push(scope);
    accordions.push(...scope.querySelectorAll?.(accordionSelector) || []);
    accordions.forEach((root) => {
      if (root.hasAttribute(initializedAttribute)) return;
      const controller = new UniversalAccordion(root);
      controllers.set(root, controller);
    });
    initializeReviews(scope);
  }

  window.UniversalPdp = { bundleIsReady, initialize, syncBundleState };

  document.addEventListener("variant:changed", (event) => {
    document.querySelectorAll(rootSelector).forEach((root) => controllers.get(root)?.onVariantChanged(event));
  });
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }
})();
